import { useEffect, useState } from "react";
import { applySeo, productJsonLd } from "../lib/seo";
import { track } from "../lib/tracking";
import { useParams, useNavigate, Link } from "react-router-dom";
import { storePath } from "../lib/storeUrl";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag, Zap, Heart, Minus, Plus, Truck, ShieldCheck, RotateCcw, Star } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "../lib/api";
import { getCart, saveCart, getWish, saveWish, addItem, cartCount, discountPct } from "../lib/cart";
import { themeVars, headingFont, SafeImg, Stars, SectionHead, ProductCard } from "../components/StoreKit";
import { setFavicon } from "../lib/utils";

const SAMPLE_REVIEWS = [
  { name: "Ananya R.", rating: 5, text: "Exceeded expectations — premium quality and fast delivery.", date: "2 weeks ago" },
  { name: "Vikram M.", rating: 4.5, text: "Great value for the price. Packaging was excellent.", date: "1 month ago" },
  { name: "Priya N.", rating: 5, text: "This is my second order. Consistently great experience.", date: "1 month ago" },
];

export default function ProductPage({ slugOverride = null }) {
  const { slug: routeSlug, pid } = useParams();
  const slug = slugOverride || routeSlug;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [selection, setSelection] = useState({});
  const [cart, setCart] = useState(() => getCart(slug));
  const [wish, setWish] = useState(() => getWish(slug));

  useEffect(() => {
    api.get(`/public/product/${pid}`).then((r) => {
      setData(r.data);
      const fav = r.data.store?.settings?.favicon_url || r.data.branding?.favicon_url;
      if (fav) setFavicon(fav);
      const sel = {};
      (r.data.product.variations || []).forEach((v) => { sel[v.name] = v.options?.[0] || ""; });
      (r.data.product.attributes || []).forEach((a) => { if (!sel[a.name]) sel[a.name] = a.options?.[0] || ""; });
      setSelection(sel);
    }).catch(() => setError("Product not found"));
  }, [pid]);
  useEffect(() => saveCart(slug, cart), [cart, slug]);
  useEffect(() => {
    if (!data) return;
    const { product: prod, store: st } = data;
    const url = window.location.origin + window.location.pathname;
    applySeo({ title: `${prod.name} | ${st.name}`, description: (prod.description || "").slice(0, 160),
               canonical: url, jsonld: [productJsonLd(prod, st.name, url)] });
    track("view_item", { value: prod.price, items: [{ id: prod.id, qty: 1 }] }, { scope: "store", slug });
  }, [data, slug]);
  useEffect(() => saveWish(slug, wish), [wish, slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-background"><p data-testid="product-error" className="text-muted-foreground">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;

  const { product: p, store, theme, related } = data;
  const colors = store.settings?.colors || theme?.colors || {};
  const vars = themeVars(colors);
  const radius = theme?.radius || "10px";
  const head = headingFont(theme);
  const pct = discountPct(p);
  const images = p.images?.length ? p.images : [theme?.preview].filter(Boolean);
  const variationStr = Object.entries(selection).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ");
  const wished = wish.some((w) => w.id === p.id);

  const hasVariants = (p.variants || []).length > 0;
  const selVariant = hasVariants
    ? p.variants.find((v) => (v.status ?? "active") === "active" && Object.entries(v.combo || {}).every(([k, val]) => selection[k] === val))
    : null;
  const displayPrice = selVariant ? Number(selVariant.sale_price || selVariant.price || p.price) : p.price;
  const displayStock = selVariant ? Number(selVariant.stock ?? p.stock) : p.stock;
  const displaySku = selVariant?.sku || p.sku;
  const optAvailable = (attrName, opt) => {
    if (!hasVariants) return true;
    return p.variants.some((v) => (v.status ?? "active") === "active" && Number(v.stock ?? 1) > 0 &&
      (v.combo || {})[attrName] === opt &&
      Object.entries(selection).every(([k, val]) => k === attrName || !val || (v.combo || {})[k] === val));
  };

  const optVariant = (attrName, opt) => {
    if (!hasVariants) return null;
    return p.variants.find((v) => (v.status ?? "active") === "active" && (v.combo || {})[attrName] === opt &&
      Object.entries(selection).every(([k, val]) => k === attrName || !val || (v.combo || {})[k] === val)) || null;
  };
  const effectiveStock = hasVariants ? (selVariant ? displayStock : 0) : p.stock;
  const cartOverrides = () => ({ price: displayPrice, image: selVariant?.image || p.images?.[0] || "" });

  const addToCart = () => {
    setCart((c) => addItem(c, p, qty, variationStr, cartOverrides()));
    toast.success(`${p.name} added to cart`, { duration: 4000, closeButton: true, action: { label: "View Cart", onClick: () => navigate(storePath(slug, "/cart")) } });
  };
  const buyNow = () => { setCart((c) => addItem(c, p, qty, variationStr, cartOverrides())); navigate(storePath(slug, "/checkout")); };
  const toggleWish = () => {
    setWish((w) => wished ? w.filter((x) => x.id !== p.id) : [...w, { id: p.id, name: p.name, price: p.price, image: p.images?.[0] || "" }]);
    toast.success(wished ? "Removed from wishlist" : "Saved to wishlist");
  };

  return (
    <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)]" data-testid="product-page">
      {/* Mini header */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link to={storePath(slug, "")} data-testid="back-to-store" className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity">
            <ArrowLeft className="h-4 w-4" /> {store.name}
          </Link>
          <button data-testid="product-cart-button" onClick={() => navigate(storePath(slug, "/cart"))}
            className="ml-auto relative h-9 px-4 rounded-full flex items-center gap-2 font-bold text-xs text-white active:scale-95 transition-transform"
            style={{ backgroundColor: "var(--sp)" }}>
            <ShoppingBag className="h-3.5 w-3.5" /> Cart
            {cartCount(cart) > 0 && <span className="h-4 min-w-4 px-1 rounded-full bg-white text-[9px] font-extrabold flex items-center justify-center" style={{ color: "var(--sp)" }}>{cartCount(cart)}</span>}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }}>
          <div className="relative overflow-hidden bg-zinc-100 group" style={{ borderRadius: radius }}>
            <SafeImg src={selVariant?.image || images[img]} alt={p.name}
              className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700" />
            {pct > 0 && <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-extrabold text-white shadow-lg" style={{ backgroundColor: "var(--sa)" }}>{pct}% OFF</span>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {images.map((src, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setImg(i)}
                  className={`h-20 w-20 overflow-hidden border-2 transition-all ${i === img ? "border-[var(--sp)]" : "border-transparent opacity-60 hover:opacity-100"}`}
                  style={{ borderRadius: radius }}>
                  <SafeImg src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Details */}
        <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
          <p className="uppercase text-[11px] tracking-[0.25em] font-extrabold" style={{ color: "var(--sa)" }}>{p.category}</p>
          <h1 data-testid="product-name" className={`${head} text-3xl sm:text-4xl tracking-tight mt-2`} style={{ color: "var(--sp)" }}>{p.name}</h1>
          <div className="flex items-center gap-2 mt-3"><Stars rating={p.rating ?? 4.5} /><span className="text-xs font-semibold" style={{ color: "var(--st)", opacity: 0.5 }}>{p.rating ?? 4.5} · {p.reviews_count ?? 128} reviews</span></div>

          <div className="flex items-baseline gap-3 mt-5">
            <span data-testid="product-price" className="font-display font-extrabold text-4xl tracking-tight" style={{ color: "var(--sp)" }}>{fmtINR(displayPrice)}</span>
            {!hasVariants && pct > 0 && <span className="text-lg line-through" style={{ color: "var(--st)", opacity: 0.4 }}>{fmtINR(p.compare_price)}</span>}
            {!hasVariants && pct > 0 && <span className="text-sm font-extrabold" style={{ color: "var(--sa)" }}>Save {pct}%</span>}
            {hasVariants && selVariant?.sale_price && selVariant?.price && Number(selVariant.sale_price) < Number(selVariant.price) && (
              <span className="text-lg line-through" style={{ color: "var(--st)", opacity: 0.4 }}>{fmtINR(selVariant.price)}</span>
            )}
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--st)", opacity: 0.5 }}>Inclusive of all taxes{displaySku ? ` · SKU ${displaySku}` : ""}</p>
          {hasVariants && (
            <p className="text-xs mt-1 font-bold" data-testid="variant-stock" style={{ color: selVariant && displayStock > 0 ? "var(--sa)" : "#dc2626" }}>
              {selVariant ? (displayStock > 0 ? `In stock — ${displayStock} available` : "Out of stock") : "Select a combination"}
            </p>
          )}

          <p className="text-sm leading-relaxed mt-5" style={{ color: "var(--st)", opacity: 0.75 }}>{p.description}</p>

          {/* Variations / Variants — universal attribute selectors */}
          {(hasVariants ? (p.attributes || []) : (p.variations || [])).map((v) => {
            const isColor = /color|shade/i.test(v.name);
            return (
            <div key={v.name} className="mt-6">
              <p className="text-xs font-extrabold uppercase tracking-widest mb-2.5" style={{ color: "var(--st)", opacity: 0.6 }}>{v.name}: <span style={{ color: "var(--sp)" }}>{selection[v.name]}</span></p>
              <div className="flex flex-wrap gap-2">
                {(v.options || []).map((opt) => {
                  const avail = optAvailable(v.name, opt);
                  const selected = selection[v.name] === opt;
                  const ov = optVariant(v.name, opt);
                  const optPrice = ov ? Number(ov.sale_price || ov.price || 0) : null;
                  if (isColor) return (
                    <button key={opt} data-testid={`variation-${v.name}-${opt}`} aria-disabled={!avail} title={avail ? opt : `${opt} — Out of Stock`}
                      onClick={() => setSelection({ ...selection, [v.name]: opt })}
                      className={`h-10 w-10 rounded-full border-2 transition-all active:scale-95 flex items-center justify-center ${!avail ? "opacity-30" : ""} ${selected ? "ring-2 ring-offset-2 ring-[var(--sp)] scale-110" : "hover:scale-105"}`}
                      style={{ backgroundColor: opt.toLowerCase(), borderColor: "rgba(0,0,0,0.15)" }}>
                      {!avail && <span className="block h-0.5 w-7 bg-red-500 rotate-45 rounded-full" data-testid={`oos-${v.name}-${opt}`} />}
                    </button>
                  );
                  return (
                  <button key={opt} data-testid={`variation-${v.name}-${opt}`} aria-disabled={!avail} onClick={() => setSelection({ ...selection, [v.name]: opt })}
                    className={`px-4 py-2 text-sm font-bold border-2 transition-all active:scale-95 ${!avail ? "opacity-60" : ""} ${selected ? "text-white" : "hover:border-[var(--sp)]"}`}
                    style={selected ? { backgroundColor: "var(--sp)", borderColor: "var(--sp)", borderRadius: radius } : { borderColor: "rgba(0,0,0,0.12)", borderRadius: radius, color: "var(--st)" }}>
                    {opt}{optPrice ? ` · ${fmtINR(optPrice)}` : ""}
                    {!avail && <span className="block text-[9px] font-extrabold uppercase tracking-wider text-red-500 mt-0.5" data-testid={`oos-${v.name}-${opt}`}>Out of Stock</span>}
                  </button>
                  );
                })}
              </div>
            </div>
            );
          })}

          {/* Qty + actions */}
          <div className="flex items-center gap-3 mt-7">
            <div className="flex items-center border-2 rounded-full" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 flex items-center justify-center"><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="w-8 text-center font-extrabold">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(Math.min(Math.max(effectiveStock, 1), qty + 1))} className="h-11 w-11 flex items-center justify-center"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="product-wishlist" onClick={toggleWish}
              className="h-11 w-11 rounded-full border-2 flex items-center justify-center transition-all hover:scale-105 active:scale-95" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
              <Heart className={`h-4 w-4 ${wished ? "fill-red-500 text-red-500" : ""}`} style={wished ? {} : { color: "var(--st)" }} />
            </button>
          </div>
          <div className="flex gap-3 mt-4">
            {effectiveStock <= 0 ? (
              <span className="text-sm font-bold text-red-500 py-3" data-testid="product-oos-label">{hasVariants && !selVariant ? "Select an option" : "Out of stock"}</span>
            ) : (
              <>
                <button data-testid="product-add-to-cart" onClick={addToCart} disabled={hasVariants && (!selVariant || displayStock < qty)}
                  className="flex-1 h-13 py-3.5 rounded-full font-bold text-sm border-2 transition-all active:scale-95 hover:bg-black/5 flex items-center justify-center gap-2"
                  style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>
                  <ShoppingBag className="h-4 w-4" /> Add to Cart
                </button>
                <button data-testid="product-buy-now" onClick={buyNow} disabled={hasVariants && (!selVariant || displayStock < qty)}
                  className="flex-1 h-13 py-3.5 rounded-full font-bold text-sm text-white transition-all active:scale-95 hover:opacity-90 shadow-lg flex items-center justify-center gap-2"
                  style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                  <Zap className="h-4 w-4" /> Buy Now
                </button>
              </>
            )}
          </div>
          <p className="text-xs font-bold mt-3" style={{ color: effectiveStock < 10 ? "#DC2626" : "#16A34A" }}>
            {effectiveStock <= 0 ? "" : effectiveStock < 10 ? `Only ${effectiveStock} left in stock!` : "In stock · ready to ship"}
          </p>

          <div className="grid grid-cols-3 gap-3 mt-8 border-t pt-6" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            {[["Free shipping over ₹999", Truck], ["Secure checkout", ShieldCheck], ["Easy 7-day returns", RotateCcw]].map(([t, Icon]) => (
              <div key={t} className="text-center">
                <Icon className="h-5 w-5 mx-auto" style={{ color: "var(--sa)" }} />
                <p className="text-[11px] font-bold mt-2" style={{ color: "var(--st)", opacity: 0.65 }}>{t}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Reviews */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <SectionHead overline="Customer reviews" title={`Rated ${p.rating ?? 4.5} out of 5`} />
        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          {SAMPLE_REVIEWS.map((r, i) => (
            <motion.div key={r.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white border border-black/5 p-6" style={{ borderRadius: radius }} data-testid={`review-${i}`}>
              <Stars rating={r.rating} />
              <p className="text-sm leading-relaxed mt-3" style={{ color: "var(--st)", opacity: 0.8 }}>"{r.text}"</p>
              <p className="text-xs font-extrabold mt-4" style={{ color: "var(--sp)" }}>{r.name} <span style={{ color: "var(--st)", opacity: 0.4 }} className="font-semibold">· {r.date}</span></p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
          <SectionHead overline="You may also like" title="Related Products" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
            {related.map((rp, i) => (
              <ProductCard key={rp.id} p={rp} delay={i * 0.05} radius={radius}
                onOpen={() => { navigate(storePath(slug, `/product/${rp.id}`)); window.scrollTo(0, 0); setImg(0); }}
                onAdd={() => { setCart((c) => addItem(c, rp, 1, "")); toast.success(`${rp.name} added to cart`, { duration: 4000, closeButton: true, action: { label: "View Cart", onClick: () => navigate(storePath(slug, "/cart")) } }); }}
                onBuy={() => { setCart((c) => addItem(c, rp, 1, "")); navigate(storePath(slug, "/checkout")); }}
                wished={wish.some((w) => w.id === rp.id)}
                onWish={() => setWish((w) => wish.some((x) => x.id === rp.id) ? w.filter((x) => x.id !== rp.id) : [...w, { id: rp.id, name: rp.name, price: rp.price, image: rp.images?.[0] || "" }])} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
